import { FixedLayout } from 'fabric';
import type { Canvas, Circle, FabricImage, FabricObject, Group, IText } from 'fabric';
import { findByPieceId, isLayerObject, PIECE } from '../../canvas/utils/canvas-pieces';
import { fromFabricFill } from '../../shared/utils/fill';
import type { TextAlign } from '../../shared/types/text-align';
import { isPillTextbox } from '../../pill/types';
import { isSobreimpresoTextbox } from '../../text/sobreimpreso/types';
import { readGroupCollection } from '../../grouping/collection/read-group-collection';
import { ANIMATION_DURATION } from '../../animations/constants';
import { getOrAssignLayerId } from '../layer-id';
import type { AnimationType } from '../../animations/types';
import type { PillTextbox } from '../../pill/types';
import type { SobreimpresoTextbox } from '../../text/sobreimpreso/types';
import type {
  Background,
  GroupChildLayer,
  GroupContent,
  ImageContent,
  Layer,
  LayerKind,
  LayerShadow,
  PieceModel,
  PillContent,
  ShapeContent,
  SobreimpresoContent,
  SvgContent,
  TextCharStyles,
  TextContent,
  TimeMs,
  VideoContent
} from '../types';

type GetVideo = (id: string) => HTMLVideoElement | null;
type VisualKind = Exclude<LayerKind, 'audio'>;

export interface FabricToModelOptions {
  width: number;
  height: number;
  type: 'image' | 'video';
  fps?: number;
  id?: string;
  getVideo?: GetVideo;
}

// ---- Detect and measure videos.

const isVideo = (obj: FabricObject, getVideo?: GetVideo): boolean => {
  if (obj.videoSrc) return true;
  if (!getVideo) return false;
  return !!obj.id && getVideo(obj.id) !== null;
};

const videoDurationMs = (obj: FabricObject, getVideo?: GetVideo): TimeMs | null => {
  if (!getVideo || !obj.id) return null;
  const el = getVideo(obj.id);
  if (!el || !isFinite(el.duration) || el.duration <= 0) return null;
  return Math.round(el.duration * 1000);
};

const detectKind = (obj: FabricObject, getVideo?: GetVideo): VisualKind | null => {
  if (isPillTextbox(obj)) return 'pill';
  if (isSobreimpresoTextbox(obj)) return 'sobreimpreso';
  if (isVideo(obj, getVideo)) return 'video';
  switch (obj.type) {
    case 'i-text':
    case 'textbox':
    case 'text':
      return 'text';
    case 'image':
      return 'image';
    case 'rect':
    case 'circle':
      return 'shape';
    case 'group':
      return 'group';
    default:
      return 'svg';
  }
};

// ---- Shared capabilities.

// Fabric v6 deprecates typed originX/originY accessors,
// but they still define the object's anchor point.
// Read them through a plain cast to avoid the deprecation warning.
type WithOrigin = { originX?: 'left' | 'center' | 'right'; originY?: 'top' | 'center' | 'bottom' };

const readTransform = (obj: FabricObject) => {
  const o = obj as WithOrigin;
  return {
    left: obj.left ?? 0,
    top: obj.top ?? 0,
    scaleX: obj.scaleX ?? 1,
    scaleY: obj.scaleY ?? 1,
    rotation: obj.angle ?? 0,
    opacity: obj.opacity ?? 1,
    originX: o.originX ?? 'center',
    originY: o.originY ?? 'center'
  };
};

const readAnimation = (obj: FabricObject) =>
  obj.assignedAnimation
    ? {
        enter: {
          preset: obj.assignedAnimation as AnimationType,
          duration: obj.animationDuration ?? ANIMATION_DURATION
        }
      }
    : {};

const readAttachable = (obj: FabricObject) => (obj.attachTo ? { attachTo: obj.attachTo } : {});
const readPieceId = (obj: FabricObject) => (obj.pieceId ? { pieceId: obj.pieceId } : {});
// No fallback assignment here (unlike getOrAssignLayerId): absence means "shared video/text
// group row" and must stay that way. Only explicit creation flows stamp a fresh laneKey.
const readLaneKey = (obj: FabricObject) => (obj.laneKey ? { laneKey: obj.laneKey } : {});
// Same reasoning as readLaneKey: no fallback. Absence means "not attached to a video" and must
// stay that way — only an explicit link (stamped upstream, e.g. build-scenes-model.ts) counts.
const readTimeAttach = (obj: FabricObject) => (obj.timeAttach ? { timeAttach: obj.timeAttach } : {});
// Same reasoning: no fallback. Absence means "user-added" and must stay that way.
const readSceneDerived = (obj: FabricObject) => (obj.sceneDerived ? { sceneDerived: true } : {});

// Fabric spreads the intent across six flags; the model keeps the intent.
const readInteraction = (obj: FabricObject) => ({
  ...(obj.lockMovementX ? { pinned: true } : {}),
  ...(obj.deletable === false ? { deletable: false } : {})
});

const readShadow = (obj: FabricObject): { shadow?: LayerShadow } => {
  const s = obj.shadow;
  if (!s) return {};
  return { shadow: { color: s.color, offsetX: s.offsetX, offsetY: s.offsetY, blur: s.blur } };
};

const isEditing = (obj: FabricObject): boolean => (obj as { isEditing?: boolean }).isEditing === true;

// In the model, an effect is active when its params are present.
const readAlphaFalloff = (obj: FabricObject) => ({
  ...(obj.alphaFalloffEnabled && obj.alphaFalloffParams ? { alphaFalloff: obj.alphaFalloffParams } : {})
});

const readChromaKey = (obj: FabricObject) => ({
  ...(obj.chromaKeyEnabled && obj.chromaKeyParams ? { chromaKey: obj.chromaKeyParams } : {})
});

// ---- When each layer appears and how long it lasts.
// Two passes: fill layers need the piece length before their own duration is known.

interface RawTiming {
  start: TimeMs;
  explicit: TimeMs | null;
  intrinsic: TimeMs | null;
  animEnd: TimeMs | null;
}

const readRawTiming = (obj: FabricObject, getVideo?: GetVideo): RawTiming => {
  const start = obj.clipStart ?? obj.animationDelay ?? 0;
  const explicit = typeof obj.clipDuration === 'number' && obj.clipDuration > 0 ? obj.clipDuration : null;
  const intrinsic = videoDurationMs(obj, getVideo);
  const animEnd = obj.assignedAnimation ? start + (obj.animationDuration ?? ANIMATION_DURATION) : null;
  return { start, explicit, intrinsic, animEnd };
};

const pieceDurationFrom = (raws: RawTiming[]): TimeMs => {
  let max = 0;
  for (const r of raws) {
    if (r.explicit !== null) max = Math.max(max, r.start + r.explicit);
    else if (r.intrinsic !== null) max = Math.max(max, r.start + r.intrinsic);
    if (r.animEnd !== null) max = Math.max(max, r.animEnd);
  }
  return max;
};

const resolveTiming = (raw: RawTiming, isVid: boolean, pieceDuration: TimeMs) => {
  if (raw.explicit !== null) return { start: raw.start, duration: raw.explicit, fit: 'fixed' as const };
  if (isVid && raw.intrinsic !== null) return { start: raw.start, duration: raw.intrinsic, fit: 'fixed' as const };
  return { start: raw.start, duration: Math.max(0, pieceDuration - raw.start), fit: 'fill' as const };
};

// ---- Content per kind.

const readTextContent = (obj: IText): TextContent => ({
  text: obj.text ?? '',
  fontFamily: obj.fontFamily ?? 'Arial',
  fontSize: obj.fontSize ?? 16,
  ...(obj.fontWeight != null ? { fontWeight: obj.fontWeight } : {}),
  ...(obj.fontStyle ? { fontStyle: obj.fontStyle as 'normal' | 'italic' } : {}),
  ...(obj.textAlign ? { textAlign: obj.textAlign as TextAlign } : {}),
  width: obj.width ?? 0,
  fill: fromFabricFill(obj.fill, '#000000'),
  ...(obj.lineHeight != null ? { lineHeight: obj.lineHeight } : {}),
  ...(obj.charSpacing != null ? { charSpacing: obj.charSpacing } : {}),
  ...(obj.underline ? { underline: obj.underline } : {}),
  ...((obj as { splitByGrapheme?: boolean }).splitByGrapheme ? { splitByGrapheme: true } : {}),
  // Persist per-char styles even when textStyleRules are set — those become the user's
  // overrides on top of the rules. See refreshTextStyleRules in model-to-fabric.
  // Clone: the model must not share the styles object with the live Fabric object.
  ...(obj.styles && Object.keys(obj.styles).length ? { styles: structuredClone(obj.styles) } : {}),
  ...(obj.autoFit ? { autoFit: obj.autoFit } : {}),
  ...(obj.wrapText ? { wrapText: obj.wrapText } : {}),
  ...(obj.fitToContent ? { fitToContent: obj.fitToContent } : {}),
  ...(obj.highlightRanges ? { highlightRanges: obj.highlightRanges } : {}),
  ...(obj.textStyleRules ? { textStyleRules: obj.textStyleRules } : {})
});

const readImageContent = (obj: FabricImage): ImageContent => ({
  src: obj.getSrc() ?? '',
  ...(obj.imageFit ? { imageFit: obj.imageFit } : {}),
  ...(obj.imageBox ? { imageBox: obj.imageBox } : {}),
  ...readAlphaFalloff(obj)
});

const readVideoContent = (obj: FabricImage, raw: RawTiming): VideoContent => ({
  src: obj.videoSrc ?? obj.getSrc() ?? '',
  intrinsicDuration: raw.intrinsic ?? 0, // filled once the video finishes loading
  ...(obj.audioEnabled != null ? { audioEnabled: obj.audioEnabled } : {}),
  ...readAlphaFalloff(obj),
  ...readChromaKey(obj)
});

const readShapeContent = (obj: FabricObject): ShapeContent => {
  const base = {
    fill: fromFabricFill(obj.fill, '#000000'),
    ...(obj.stroke ? { stroke: obj.stroke as string } : {}),
    // Preserve strokeWidth even when it is 0, since 0 is a valid value.
    ...(obj.strokeWidth != null ? { strokeWidth: obj.strokeWidth } : {})
  };
  if (obj.type === 'circle') return { ...base, shape: 'circle', radius: (obj as Circle).radius ?? 0 };
  const rx = (obj as { rx?: number }).rx ?? 0;
  return {
    ...base,
    shape: 'rect',
    width: obj.width ?? 0,
    height: obj.height ?? 0,
    ...(rx > 0 ? { cornerRadius: rx } : {})
  };
};

const readPillContent = (obj: PillTextbox) => ({
  ...obj.pill,
  text: obj.text ?? '',
  fontFamily: obj.fontFamily ?? 'Arial',
  fontSize: obj.fontSize ?? 16,
  ...(obj.fontWeight != null ? { fontWeight: obj.fontWeight } : {}),
  ...(obj.fontStyle ? { fontStyle: obj.fontStyle as 'normal' | 'italic' } : {}),
  ...(obj.textAlign ? { textAlign: obj.textAlign as TextAlign } : {}),
  // No width: the pill derives it from text + padding, so storing it is dead data.
  textFill: (obj.fill as string) ?? '#000000'
});

const readSvgContent = (obj: FabricObject): SvgContent => ({ json: obj.toObject() });

const readSobreimpresoContent = (obj: SobreimpresoTextbox): SobreimpresoContent => ({
  ...obj.sobreimpreso,
  text: obj.text ?? '',
  fontFamily: obj.fontFamily ?? 'Arial',
  fontSize: obj.fontSize ?? 16,
  ...(obj.fontWeight != null ? { fontWeight: obj.fontWeight } : {}),
  ...(obj.fontStyle ? { fontStyle: obj.fontStyle as 'normal' | 'italic' } : {}),
  ...(obj.textAlign ? { textAlign: obj.textAlign as TextAlign } : {}),
  width: obj.width ?? 0,
  textFill: (obj.fill as string) ?? '#000000',
  ...(obj.styles && Object.keys(obj.styles).length ? { styles: structuredClone(obj.styles) } : {})
});

// A group's children carry coords relative to the group; readTransform reads those as-is.
const readGroupContent = (group: Group, pieceDuration: TimeMs, getVideo?: GetVideo): GroupContent => {
  const collection = readGroupCollection(group);
  return {
    children: readGroupChildren(group, pieceDuration, getVideo, Boolean(collection)),
    ...(group.subTargetCheck ? { subTargetCheck: true } : {}),
    ...(group.layoutManager?.strategy instanceof FixedLayout ? { layout: 'fixed' as const } : {}),
    ...(collection ? { collection } : {}),
    ...readAlphaFalloff(group)
  };
};

const readGroupChildren = (
  group: Group,
  pieceDuration: TimeMs,
  getVideo: GetVideo | undefined,
  isCollection: boolean
): GroupChildLayer[] =>
  group
    .getObjects()
    .map(child => ({ child, kind: detectKind(child, getVideo) }))
    .filter((e): e is { child: FabricObject; kind: VisualKind } => e.kind !== null)
    .map(({ child, kind }) => {
      const layer = buildLayer(child, kind, readRawTiming(child, getVideo), pieceDuration, getVideo) as GroupChildLayer;
      // A collection hides inactive slots with Fabric's `visible`; that state is persistent here, not layerVisible.
      return isCollection ? ({ ...layer, visible: child.visible !== false } as GroupChildLayer) : layer;
    });

type ContentCtx = { raw: RawTiming; pieceDuration: TimeMs; getVideo?: GetVideo };

type ContentByKind = {
  text: TextContent;
  image: ImageContent;
  video: VideoContent;
  shape: ShapeContent;
  pill: PillContent;
  sobreimpreso: SobreimpresoContent;
  group: GroupContent;
  svg: SvgContent;
};

const CONTENT_READERS: { [K in VisualKind]: (obj: FabricObject, ctx: ContentCtx) => ContentByKind[K] } = {
  text: obj => readTextContent(obj as IText),
  image: obj => readImageContent(obj as FabricImage),
  video: (obj, { raw }) => readVideoContent(obj as FabricImage, raw),
  shape: obj => readShapeContent(obj),
  pill: obj => readPillContent(obj as PillTextbox),
  sobreimpreso: obj => readSobreimpresoContent(obj as SobreimpresoTextbox),
  group: (obj, { pieceDuration, getVideo }) => readGroupContent(obj as Group, pieceDuration, getVideo),
  svg: obj => readSvgContent(obj)
};

const buildLayer = (
  obj: FabricObject,
  kind: VisualKind,
  raw: RawTiming,
  pieceDuration: TimeMs,
  getVideo?: GetVideo
): Layer => {
  const common = {
    id: getOrAssignLayerId(obj),
    visible: obj.layerVisible !== false,
    // While a text is being edited Fabric sets selectable=false; that's transient, not a lock.
    locked: obj.selectable === false && !isEditing(obj),
    ...readPieceId(obj),
    ...readLaneKey(obj),
    ...readTimeAttach(obj),
    ...readSceneDerived(obj),
    ...readTransform(obj),
    ...resolveTiming(raw, kind === 'video', pieceDuration),
    ...readAnimation(obj),
    ...readAttachable(obj),
    ...readInteraction(obj),
    ...readShadow(obj)
  };
  const content = CONTENT_READERS[kind](obj, { raw, pieceDuration, getVideo });
  return { ...common, kind, content } as Layer;
};

const readBackground = (canvas: Canvas): Background | undefined => {
  const bg = findByPieceId(canvas, PIECE.BACKGROUND);
  if (!bg) return undefined;
  if (bg.type === 'image') return { kind: 'image', src: (bg as FabricImage).getSrc() };
  return typeof bg.fill === 'string' ? { kind: 'color', color: bg.fill } : undefined;
};

const scaleCharStyles = (styles: TextCharStyles, k: number): TextCharStyles => {
  const out: TextCharStyles = {};
  for (const line of Object.keys(styles)) {
    out[line] = {};
    for (const char of Object.keys(styles[line])) {
      const s = styles[line][char];
      out[line][char] = typeof s.fontSize === 'number' ? { ...s, fontSize: Math.round(s.fontSize * k) } : { ...s };
    }
  }
  return out;
};

// ---- Minimize: drop geometry the renderer re-derives (see runReflow), or bake a gesture into size.

const bakeTextScale = (layer: Layer): Layer => {
  if (layer.kind !== 'text' && layer.kind !== 'sobreimpreso') return layer;
  const { scaleX, scaleY, content } = layer;
  if (scaleX === 1 && scaleY === 1) return layer;

  const unscaled = { scaleX: 1, scaleY: 1 };
  const sized = {
    fontSize: content.fontSize * scaleY,
    ...(content.width != null ? { width: content.width * scaleX } : {}),
    ...(content.styles ? { styles: scaleCharStyles(content.styles, scaleY) } : {})
  };
  return layer.kind === 'text'
    ? { ...layer, ...unscaled, content: { ...layer.content, ...sized } }
    : { ...layer, ...unscaled, content: { ...layer.content, ...sized } };
};

// A text layer manages its own width when it fits itself; otherwise a chain fit derives it.
const ownsWidth = (c: TextContent): boolean => Boolean(c.fitToContent || c.autoFit || c.wrapText);

// Every pieceId something attaches to. An anchor has no attachTo of its own but the reflow
// still repositions and refits it as part of the chain, so its geometry is derived too.
const collectAnchorIds = (layers: Layer[], into: Set<string>): void => {
  for (const layer of layers) {
    if (layer.kind === 'audio') continue;
    if (layer.attachTo) into.add(layer.attachTo.pieceId);
    if (layer.kind === 'group') collectAnchorIds(layer.content.children, into);
  }
};

// Drops geometry the reflow recomputes on load: a follower's position, and a chain textbox's width.
const dropReflowGeometry = (layer: Layer, anchorIds: Set<string>): Layer => {
  if (layer.kind === 'group') {
    const chainGroup = layer.content.children.some(c => c.attachTo);
    const collectionGroup = Boolean(layer.content.collection);
    const children = layer.content.children.map(c => {
      const stripped = dropReflowGeometry(c, anchorIds);
      if (collectionGroup) return dropCollectionGeometry(stripped) as GroupChildLayer;
      return (chainGroup ? dropPosition(stripped) : stripped) as GroupChildLayer;
    });
    layer = { ...layer, content: { ...layer.content, children } };
  }
  if (layer.kind === 'audio') return layer;

  let next = layer.attachTo ? dropLeft(layer) : layer;
  const inChain = Boolean(layer.attachTo) || (layer.pieceId != null && anchorIds.has(layer.pieceId));
  if (inChain && next.kind === 'text' && !ownsWidth(next.content)) {
    const { width: _drop, ...content } = next.content;
    next = { ...next, content };
  }
  return next;
};

const dropPosition = (layer: Layer): Layer => {
  if (layer.kind === 'audio') return layer;
  const next = { ...layer };
  delete next.left;
  delete next.top;
  return next;
};

const dropLeft = (layer: Layer): Layer => {
  if (layer.kind === 'audio') return layer;
  const next = { ...layer };
  delete next.left;
  return next;
};

const dropImageFitScale = (layer: Layer): Layer => {
  if (layer.kind !== 'image') return layer;
  const { imageFit, imageBox } = layer.content;
  if (!imageFit || imageFit === 'none' || !imageBox) return layer;
  return { ...layer, scaleX: 1, scaleY: 1 };
};

const dropCollectionGeometry = (layer: Layer): Layer => {
  if (layer.kind === 'audio') return layer;
  const next = { ...layer, scaleX: 1, scaleY: 1 };
  delete next.left;
  delete next.top;
  return next;
};

const minimizeLayer = (layer: Layer, anchorIds: Set<string>): Layer =>
  dropImageFitScale(dropReflowGeometry(bakeTextScale(layer), anchorIds));

/**
 * Reads the canvas and builds the piece model. Audio isn't a canvas object, so it
 * enters the model elsewhere.
 */
export const fabricToModel = (canvas: Canvas, opts: FabricToModelOptions): PieceModel => {
  const { width, height, type, fps = 30, id = 'main', getVideo } = opts;

  const entries = canvas
    .getObjects()
    .filter(isLayerObject)
    .map(obj => ({ obj, kind: detectKind(obj, getVideo) }))
    .filter((e): e is { obj: FabricObject; kind: VisualKind } => e.kind !== null)
    .map(e => ({ ...e, raw: readRawTiming(e.obj, getVideo) }));

  const duration = pieceDurationFrom(entries.map(e => e.raw));

  const built = entries.map(({ obj, kind, raw }) => buildLayer(obj, kind, raw, duration, getVideo));

  // Anchors are known only once every layer is built, so minimize in a second pass.
  const anchorIds = new Set<string>();
  collectAnchorIds(built, anchorIds);

  const layers: Record<string, Layer> = {};
  const layerOrder: string[] = []; // back to front, as the canvas stack already is
  for (const layer of built) {
    const minimized = minimizeLayer(layer, anchorIds);
    layers[minimized.id] = minimized;
    layerOrder.push(minimized.id);
  }

  const background = readBackground(canvas);

  return { id, width, height, type, fps, duration, ...(background ? { background } : {}), layers, layerOrder };
};
